import { canInsertNode, mergeAttributes } from "@tiptap/core"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"
import TiptapHorizontalRule from "@tiptap/extension-horizontal-rule"

function parentAllowsRule($from, ruleType, indexOffset) {
  if ($from.depth === 0) return false

  const parent = $from.node($from.depth - 1)
  const index = $from.index($from.depth - 1) + indexOffset
  return parent.canReplaceWith(index, index, ruleType)
}

function findNearestRulePos(doc, aroundPos, ruleType) {
  let nearest = null
  let nearestDistance = Infinity

  doc.descendants((node, pos) => {
    if (node.type !== ruleType) return

    const distance = Math.abs(pos - aroundPos)
    if (distance < nearestDistance) {
      nearest = pos
      nearestDistance = distance
    }
  })

  return nearest
}

function placeCaretAfterRule(tr, rulePos, rule, paragraphType) {
  const afterRule = rulePos + rule.nodeSize
  const $after = tr.doc.resolve(Math.min(afterRule, tr.doc.content.size))

  if ($after.nodeAfter?.isTextblock) {
    tr.setSelection(TextSelection.create(tr.doc, afterRule + 1))
  } else if ($after.nodeAfter?.isBlock) {
    tr.setSelection(NodeSelection.create(tr.doc, afterRule))
  } else if (paragraphType) {
    const paragraph = paragraphType.create()
    tr.insert(afterRule, paragraph)
    tr.setSelection(TextSelection.create(tr.doc, afterRule + 1))
  }
}

export const HorizontalRule = TiptapHorizontalRule.extend({
  addCommands() {
    return {
      setHorizontalRule:
        () =>
        ({ state, tr, dispatch }) => {
          if (!canInsertNode(state, state.schema.nodes[this.name])) {
            return false
          }

          const { $from, empty } = state.selection
          const rule = this.type.create()
          const replaceEmptyParagraph =
            $from.parent.type.name === "paragraph" && $from.parent.content.size === 0
          const atEndOfBlock = $from.parentOffset === $from.parent.content.size
          const atStartOfBlock = $from.parentOffset === 0

          if (dispatch) {
            let rulePos

            if (replaceEmptyParagraph) {
              const from = $from.before()
              tr.replaceWith(from, $from.after(), rule)
              rulePos = tr.mapping.map(from, -1)
            } else if (empty && atEndOfBlock && parentAllowsRule($from, this.type, 1)) {
              rulePos = $from.after()
              tr.insert(rulePos, rule)
            } else if (empty && atStartOfBlock && parentAllowsRule($from, this.type, 0)) {
              rulePos = $from.before()
              tr.insert(rulePos, rule)
            } else {
              const from = $from.pos
              tr.replaceSelectionWith(rule)
              rulePos = findNearestRulePos(tr.doc, tr.mapping.map(from), this.type)
            }

            if (rulePos == null) return true

            placeCaretAfterRule(tr, rulePos, rule, state.schema.nodes.paragraph)
            tr.scrollIntoView()
          }

          return true
        },
    }
  },

  parseHTML() {
    return [
      // Claim our own serialized wrapper with a priority above the generic
      // html-preservation Div node, so save/reopen round trips don't nest an
      // extra div around the rule each time.
      { tag: `div[data-type="${this.name}"]`, priority: 100 },
      { tag: "hr" },
    ]
  },

  renderHTML() {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, { "data-type": this.name }),
      ["hr"],
    ];
  },
})

export default HorizontalRule
